import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  BookOpen,
  Book,
  FileSpreadsheet,
  BarChart3,
  Upload,
  LogOut,
  Menu,
  X,
  ClipboardList,
  Settings,
  Activity,
  UserPlus,
  Download,
  Printer,
  DollarSign,
  ShoppingBasket,
  CalendarDays,
  ChevronUp,
} from "lucide-react";
import { Button } from "./ui/button";

const LOGO_URL = "https://cresabel.co.zw/Cresabel%20Logo.png";

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  const adminNavItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/users", label: "Users", icon: UserPlus },
    { path: "/teachers", label: "Teachers", icon: Users },
    { path: "/students", label: "Students", icon: GraduationCap },
    { path: "/classes", label: "Classes", icon: BookOpen },
    { path: "/subjects", label: "Subjects", icon: Book },
    { path: "/fees", label: "Fees", icon: DollarSign },
    { path: "/results", label: "Results", icon: FileSpreadsheet },
    { path: "/groceries", label: "Groceries", icon: ShoppingBasket },
    { path: "/results-analytics", label: "Analytics", icon: BarChart3 },
    { path: "/term-management", label: "Terms", icon: CalendarDays },
    { path: "/print-results", label: "Print Results", icon: Printer },
    { path: "/export", label: "Export", icon: Download },
    { path: "/activity-logs", label: "Logs", icon: Activity },
    { path: "/trips", label: "Trips", icon: ClipboardList },
  ];

  const teacherNavItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/students", label: "Students", icon: GraduationCap },
    { path: "/results", label: "Results", icon: FileSpreadsheet },
    { path: "/groceries", label: "Groceries", icon: ShoppingBasket },
  ];

  const studentNavItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/my-results", label: "My Results", icon: ClipboardList },
    { path: "/my-fees", label: "My Fees", icon: DollarSign },
    { path: "/my-groceries", label: "My Groceries", icon: ShoppingBasket },
  ];

  const accountantNavItems = [
    { path: "/accountant/dashboard", label: "Finance Dashboard", icon: LayoutDashboard },
    { path: "/accountant/fees", label: "Fees Payment", icon: DollarSign },
    { path: "/accountant/expenses", label: "Expenses", icon: ClipboardList },
    { path: "/accountant/salaries", label: "Teacher Salaries", icon: Users },
    { path: "/accountant/statutory", label: "Statutory Charges", icon: Activity },
    { path: "/accountant/grocery-cash", label: "Grocery Cash", icon: ShoppingBasket },
    { path: "/accountant/uniforms", label: "Uniforms", icon: BookOpen },
    { path: "/accountant/trips", label: "Trip Finance", icon: ClipboardList },
    { path: "/accountant/reports", label: "Reports", icon: FileSpreadsheet },
  ];

  const getNavItems = () => {
    switch (user?.role) {
      case "admin":
        return adminNavItems;
      case "teacher":
        return teacherNavItems;
      case "student":
        return studentNavItems;
      case "accountant":
        return accountantNavItems;
      default:
        return [];
    }
  };

  const navItems = getNavItems();

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;

    return (
      <Link
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
          isActive
            ? "bg-pink-500 text-white"
            : "text-slate-400 hover:bg-slate-800 hover:text-white"
        }`}
        data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
      >
        <Icon size={14} strokeWidth={1.5} className="shrink-0" />
        <span className="font-medium text-xs truncate">{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-slate-900 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="Cresabel" className="h-8 w-8 rounded-full object-cover" />
          <span className="text-white font-heading font-bold text-sm">Cresabel</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white hover:bg-slate-800"
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-48 bg-slate-900 z-40 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo - Smaller */}
        <div className="h-14 flex items-center px-4 border-b border-slate-800">
          <img src={LOGO_URL} alt="Cresabel" className="h-8 w-8 rounded-full object-cover" />
          <span className="ml-2 text-white font-heading font-bold text-base">Cresabel</span>
        </div>

        {/* Navigation */}
        <nav className="p-2 space-y-0.5">
          {navItems.map((item) => (
            <NavLink key={item.path} item={item} />
          ))}
        </nav>

        {/* User Info & Actions */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-slate-800">
          {accountOpen && (
            <div className="p-2 space-y-0.5 border-b border-slate-800 bg-slate-800/50">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { navigate('/settings'); setSidebarOpen(false); setAccountOpen(false); }}
                className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
                data-testid="settings-btn"
              >
                <Settings size={14} className="mr-2" />
                <span className="text-xs">Settings</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
                data-testid="logout-btn"
              >
                <LogOut size={14} className="mr-2" />
                <span className="text-xs">Logout</span>
              </Button>
            </div>
          )}
          <button
            onClick={() => setAccountOpen(!accountOpen)}
            className="w-full flex items-center gap-2 p-3 hover:bg-slate-800 transition-colors"
            data-testid="account-toggle"
          >
            <div className="w-8 h-8 rounded-full bg-pink-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
              {user?.username?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-white font-medium text-sm truncate">{user?.username}</p>
              <p className="text-slate-400 text-xs capitalize">{user?.role}</p>
            </div>
            <ChevronUp size={14} className={`text-slate-400 transition-transform ${accountOpen ? '' : 'rotate-180'}`} />
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-48 min-h-screen pt-14 lg:pt-0">
        <div className="p-4 lg:p-6 animate-fade-in">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
